import React, { useState } from 'react';
import { FaPlane, FaGlobe } from 'react-icons/fa';
import { Link } from 'react-router-dom';
// Actual destination images provided by the user
import imgBali from '../../assets/images/Rectangle 26052.png';
import imgSingapore from '../../assets/images/Rectangle 26053.png';
import imgEurope from '../../assets/images/Rectangle 26054.png';

const FaPlaneIcon = FaPlane as any;
const FaGlobeIcon = FaGlobe as any;

interface CustomizedHolidaysProps {
  data?: {
    title?: string;
    international?: Array<{
      title: string;
      description: string;
      cta_text: string;
      cta_link: string;
      image_url?: string;
    }>;
    national?: Array<{
      title: string;
      description: string;
      cta_text: string;
      cta_link: string;
      image_url?: string;
    }>;
    air_ticketing?: {
      title?: string;
      description?: string;
      cta?: {
        text?: string;
        link?: string;
      };
      items?: Array<{
        title: string;
        cta_text: string;
        cta_link: string;
        icon_url?: string;
      }>;
    };
  };
}

interface AirTicketingItemProps {
  item: {
    title: string;
    cta_text: string;
    cta_link: string;
    icon_url?: string;
  };
  idx: number;
  handleCtaClick: (link: string) => void;
}

const AirTicketingItem: React.FC<AirTicketingItemProps> = ({ item, idx, handleCtaClick }) => {
  const [imageError, setImageError] = useState(false);

  // If a custom icon is provided from the CMS and hasn't failed to load,
  // we render it transparently without any surrounding red background or padding.
  const showCustomIcon = item.icon_url && !imageError;

  return (
    <div className="flex items-center gap-4">
      <div 
        className={`w-12 h-12 rounded-[10px] flex items-center justify-center flex-shrink-0 overflow-hidden ${
          showCustomIcon 
            ? 'bg-transparent p-0' 
            : 'bg-[#c1272d] text-white text-xl p-2'
        }`}
      >
        {showCustomIcon ? (
          <img 
            src={item.icon_url} 
            alt={item.title} 
            className="w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        ) : (
          idx === 0 ? <FaPlaneIcon className="transform -rotate-45" /> : <FaGlobeIcon />
        )}
      </div>
      <div>
        <h4 className="text-[#0a1e3f] font-bold text-[15px]">{item.title}</h4>
        <button 
          onClick={() => handleCtaClick(item.cta_link)} 
          className="text-[#5e6d7c] text-[13px] hover:text-[#c1272d] transition-colors bg-transparent border-none p-0 cursor-pointer text-left font-semibold"
        >
          {item.cta_text}
        </button>
      </div>
    </div>
  );
};

const CustomizedHolidays: React.FC<CustomizedHolidaysProps> = ({ data }) => {
  const [activeTab, setActiveTab] = useState<'international' | 'national'>('international');

  // Fallback lists if API data is incomplete
  const fallbackInternational = [
    {
      title: 'Travel to Bali',
      description: 'We offer tailored travel experiences perfect for families, groups, or solo travelers seeking stress-free vacations.',
      image: imgBali,
      cta_text: 'CONTACT US',
      cta_link: '/contact'
    },
    {
      title: 'Travel to Singapore',
      description: 'We offer tailored travel experiences perfect for families, groups, or solo travelers seeking stress-free vacations.',
      image: imgSingapore,
      cta_text: 'CONTACT US',
      cta_link: '/contact'
    },
    {
      title: 'Travel to Europe',
      description: 'We offer tailored travel experiences perfect for families, groups, or solo travelers seeking stress-free vacations.',
      image: imgEurope,
      cta_text: 'CONTACT US',
      cta_link: '/contact'
    }
  ];

  const fallbackNational = [
    {
      title: 'Travel to Mathura',
      description: 'We offer tailored travel experiences perfect for families, groups, or solo travelers seeking stress-free vacations.',
      image: imgBali,
      cta_text: 'CONTACT US',
      cta_link: '/contact'
    },
    {
      title: 'Travel to Jaipur',
      description: 'We offer tailored travel experiences perfect for families, groups, or solo travelers seeking stress-free vacations.',
      image: imgSingapore,
      cta_text: 'CONTACT US',
      cta_link: '/contact'
    },
    {
      title: 'Travel to Mumbai',
      description: 'We offer tailored travel experiences perfect for families, groups, or solo travelers seeking stress-free vacations.',
      image: imgEurope,
      cta_text: 'CONTACT US',
      cta_link: '/contact'
    }
  ];

  // Resolve packages from API or use fallbacks
  const getPackages = () => {
    if (activeTab === 'international') {
      const items = data?.international || [];
      if (items.length > 0) {
        return items.map((item, idx) => ({
          title: item.title,
          description: item.description,
          image: item.image_url || [imgBali, imgSingapore, imgEurope][idx % 3],
          cta_text: item.cta_text,
          cta_link: item.cta_link
        }));
      }
      return fallbackInternational;
    } else {
      const items = data?.national || [];
      if (items.length > 0) {
        return items.map((item, idx) => ({
          title: item.title,
          description: item.description,
          image: item.image_url || [imgBali, imgSingapore, imgEurope][idx % 3],
          cta_text: item.cta_text,
          cta_link: item.cta_link
        }));
      }
      return fallbackNational;
    }
  };

  const displayedDestinations = getPackages();

  // Resolve Air Ticketing content
  const airTitle = data?.air_ticketing?.title || "Air Ticketing";
  const airDesc = data?.air_ticketing?.description || "Discover a wide range of value-added services, from document attestation.";
  const airCtaText = data?.air_ticketing?.cta?.text || "CALL NOW +91-00000000";
  const airCtaLink = data?.air_ticketing?.cta?.link || "tel:+91000000000";
  const airItems = data?.air_ticketing?.items || [
    {
      title: "Domestic Flights",
      cta_text: "View Flights",
      cta_link: "/contact"
    },
    {
      title: "International Flights",
      cta_text: "View Flights",
      cta_link: "/contact"
    }
  ];

  // CTA navigation handler to bypass external absolute URL router errors
  const handleCtaClick = (link: string) => {
    if (link.startsWith('http://') || link.startsWith('https://') || link.startsWith('tel:')) {
      window.location.href = link;
    } else {
      window.location.href = '/contact';
    }
  };

  return (
    <section className="py-10 md:py-16 bg-white font-manrope">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Left Column: Air Ticketing */}
          <div className="lg:col-span-1 bg-[#F1F4F8] rounded-[16px] p-6 lg:p-8 flex flex-col">
            <h3 className="text-[#0a1e3f] text-2xl font-extrabold mb-3">{airTitle}</h3>
            <p className="text-[#5e6d7c] text-[14px] leading-relaxed mb-8">
              {airDesc}
            </p>

            <div className="flex flex-col gap-6 flex-grow">
              {airItems.map((item, idx) => (
                <AirTicketingItem 
                  key={idx}
                  item={item}
                  idx={idx}
                  handleCtaClick={handleCtaClick}
                />
              ))}
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => handleCtaClick(airCtaLink)}
              className="mt-10 block w-full bg-[#c1272d] hover:bg-red-700 text-white font-bold text-[13px] py-4 rounded-[8px] transition-colors active:scale-95 shadow-md uppercase tracking-wider"
            >
              {airCtaText}
            </button>
          </div>

          {/* Right Column: Holiday Packages */}
          <div className="lg:col-span-3">
            {/* Header Row */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
              <h2 className="text-[#0a1e3f] text-3xl lg:text-[40px] font-extrabold leading-tight max-w-2xl">
                {data?.title || "Customized Holiday Packages in India and Abroad"}
              </h2>

              {/* Toggle Switch */}
              <div className="flex items-center bg-[#F1F4F8] rounded-full p-1 self-start md:self-auto flex-shrink-0">
                <button
                  onClick={() => setActiveTab('international')}
                  className={`px-6 py-2.5 rounded-full text-[14px] font-bold transition-all duration-300 ${
                    activeTab === 'international' 
                      ? 'bg-[#c1272d] text-white shadow-md' 
                      : 'bg-transparent text-[#0a1e3f] hover:text-[#c1272d]'
                  }`}
                >
                  International
                </button>
                <button
                  onClick={() => setActiveTab('national')}
                  className={`px-6 py-2.5 rounded-full text-[14px] font-bold transition-all duration-300 ${
                    activeTab === 'national' 
                      ? 'bg-[#c1272d] text-white shadow-md' 
                      : 'bg-transparent text-[#0a1e3f] hover:text-[#c1272d]'
                  }`}
                >
                  National
                </button>
              </div>
            </div>

            {/* Destinations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedDestinations.map((dest, idx) => (
                <div 
                  key={idx} 
                  className="flex flex-col group cursor-pointer"
                  onClick={() => handleCtaClick(dest.cta_link)}
                >
                  <div className="rounded-[16px] overflow-hidden mb-5 h-[220px] shadow-sm">
                    <img 
                      src={dest.image} 
                      alt={dest.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <h4 className="text-[#0a1e3f] text-xl font-extrabold mb-2 group-hover:text-[#c1272d] transition-colors">
                    {dest.title}
                  </h4>
                  <p className="text-[#5e6d7c] text-[13px] leading-relaxed mb-4 flex-grow">
                    {dest.description}
                  </p>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCtaClick(dest.cta_link);
                    }}
                    className="text-[#c1272d] font-bold text-[13px] tracking-wider uppercase mt-auto text-left bg-transparent border-none p-0 hover:underline"
                  >
                    {dest.cta_text || "CONTACT US"}
                  </button>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default CustomizedHolidays;

